import { TEXT_FILE_REGEX } from '../../../utils/fileUtils';
import { uploadFile, UPLOAD_LIMIT } from '../../../firebase/storage';
import { uploadSharedAsset } from '../../../firebase/sharing';
import { handleCreateFile } from './files';
import { showErrorModal } from './ide';
import { incrementAssetTotalSize, adjustTotalSize } from './assets';

const MAX_LOCAL_FILE_SIZE = 80000; // bytes, aka 80 KB
// Per-file cap for shared-editor uploads (must match SHARED_UPLOAD_LIMIT in functions/index.js).
const SHARED_UPLOAD_LIMIT = 12 * 1024 * 1024;

function isRemoteUpload(file) {
  return !TEXT_FILE_REGEX.test(file.name) || file.size >= MAX_LOCAL_FILE_SIZE;
}

export async function dropzoneAcceptCallback(
  userId,
  file,
  done,
  dispatch,
  totalSize,
  // When set ({ projectId, token }), the user is an edit-link recipient — media goes to the OWNER's
  // storage via the uploadSharedAsset function instead of the recipient's own storage.
  sharedEdit = null
) {
  // if a user would want to edit this file as text, local interceptor
  if (!isRemoteUpload(file)) {
    try {
      // eslint-disable-next-line no-param-reassign
      file.content = await file.text();
      // Make it an error so that it won't be sent anywhere, but style as a success.
      done('Uploading plaintext file locally.');
      file.previewElement.classList.remove('dz-error');
      file.previewElement.classList.add('dz-success');
      file.previewElement.classList.add('dz-processing');
      file.previewElement.querySelector('.dz-upload').style.width = '100%';
    } catch (error) {
      done(`Failed to download file ${file.name}: ${error}`);
      console.warn(file);
    }
  } else if (sharedEdit) {
    // Edit-link recipient: route the media to the OWNER's storage via the function.
    if (file.size > SHARED_UPLOAD_LIMIT) {
      done(
        'This image is too large to add to a shared sketch (max 12 MB). Ask the owner to add it.'
      );
      return;
    }
    try {
      const result = await uploadSharedAsset(
        sharedEdit.projectId,
        sharedEdit.token,
        file
      );
      // eslint-disable-next-line no-param-reassign
      file.firebaseUrl = result.url;
      // The asset lives in the OWNER's storage/quota — don't bump the recipient's totals.
      done('Uploaded to the sketch owner’s storage.');
      file.previewElement.classList.remove('dz-error');
      file.previewElement.classList.add('dz-success');
      file.previewElement.classList.add('dz-processing');
      file.previewElement.querySelector('.dz-upload').style.width = '100%';
    } catch (error) {
      done(error?.message || 'Upload failed.');
    }
  } else {
    // quota pre-check before hitting Firebase Storage
    if ((totalSize || 0) + file.size > UPLOAD_LIMIT) {
      if (dispatch) {
        dispatch(showErrorModal('uploadLimit'));
      }
      done('Upload limit reached.');
      return;
    }
    try {
      const result = await uploadFile(userId, file);
      // eslint-disable-next-line no-param-reassign
      file.firebaseUrl = result.url;
      // eslint-disable-next-line no-param-reassign
      file.firebaseSize = result.size;
      // Make it an error so that it won't be POSTed, but style as a success.
      done('Uploaded to Firebase Storage.');
      file.previewElement.classList.remove('dz-error');
      file.previewElement.classList.add('dz-success');
      file.previewElement.classList.add('dz-processing');
      file.previewElement.querySelector('.dz-upload').style.width = '100%';
    } catch (error) {
      // Genuine upload failure (network, Storage rule, etc.). The cumulative-quota case is already
      // handled by the pre-check above, so do NOT show the "upload limit" modal here — surface the
      // error inline on the Dropzone preview instead.
      done(error?.message || 'Upload failed.');
    }
  }
}

export function dropzoneCompleteCallback(file) {
  return (dispatch) => {
    // A Firebase upload completes via the done('message') trick, which sets file.status to 'error';
    // presence of firebaseUrl (set only on a successful upload) is the real success signal.
    if (file.firebaseUrl) {
      const formParams = {
        name: file.name,
        url: file.firebaseUrl
      };
      dispatch(handleCreateFile(formParams, false));
      // Optimistic usage bump so the AssetSize bar and the upload-quota pre-check (which read
      // assets.totalSize) reflect the new usage immediately. getAssets() recomputes the
      // authoritative total when the Assets tab opens.
      if (file.firebaseSize) {
        dispatch(incrementAssetTotalSize(file.firebaseSize));
        // Keep users/{uid}.totalSize current in Redux AND Firestore so the cumulative quota gate is
        // accurate in-session and survives reload.
        dispatch(adjustTotalSize(file.firebaseSize));
      }
    } else if (file.content !== undefined) {
      const formParams = {
        name: file.name,
        content: file.content
      };
      dispatch(handleCreateFile(formParams, false));
    } else if (file.status === 'error') {
      file.previewElement.classList.add('dz-error');
      file.previewElement.classList.remove('dz-success');
    }
  };
}
